// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import type { Facility } from '../types';
import { el, icon, clear } from '../dom';
import { formatMass, formatYear } from '../format';
import { searchFacilities } from '../analysis';
import { divisionColor, stateColor } from '../colors';

export function renderSearch(root: HTMLElement, ctx: AppContext) {
  root.appendChild(el('h1', { class: 'section-title' }, 'Search'));
  root.appendChild(el('p', { class: 'section-sub' }, [
    'Type a suburb, postcode, facility name, or business name to find any reporting site nationwide. Defaults to top emitters by lifetime release.'
  ]));

  const input = el('input', {
    type: 'search',
    placeholder: 'Try "Newcastle", "BHP", or "3000" …',
    autocomplete: 'off',
    'aria-label': 'Search facilities'
  });

  const stateSelect = el('select', { 'aria-label': 'Filter by state' }, [
    el('option', { value: '' }, 'All states'),
    el('option', { value: 'NSW' }, 'New South Wales'),
    el('option', { value: 'VIC' }, 'Victoria'),
    el('option', { value: 'QLD' }, 'Queensland'),
    el('option', { value: 'WA' }, 'Western Australia'),
    el('option', { value: 'SA' }, 'South Australia'),
    el('option', { value: 'TAS' }, 'Tasmania'),
    el('option', { value: 'ACT' }, 'Australian Capital Territory'),
    el('option', { value: 'NT' }, 'Northern Territory')
  ]);

  const yearSelect = el('select', { 'aria-label': 'Filter by reporting year' }, [
    el('option', { value: 'any' }, 'Any reporting year'),
    el('option', { value: 'latest' }, `Reported in ${formatYear(ctx.data.meta.latest_year)}`),
    el('option', { value: '5y' }, 'Reported in the last 5 years')
  ]);

  const controls = el('div', { class: 'control-row' }, [
    el('div', { class: 'search-bar' }, [el('span', { class: 'search-icon' }, [icon('search', 16)]), input]),
    el('div', { class: 'field' }, [el('label', null, 'State'), stateSelect]),
    el('div', { class: 'field' }, [el('label', null, 'Reporting'), yearSelect]),
    el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-sm)' }, '8,140 facilities indexed')
  ]);
  root.appendChild(controls);

  const results = el('div');
  root.appendChild(results);

  let timer: number | undefined;
  function update() {
    const q = input.value;
    const state = stateSelect.value;
    const yearMode = yearSelect.value;
    const latest = ctx.data.meta.latest_year;

    const facilities = ctx.data.facilities.filter(f => {
      if (state && f.state !== state) return false;
      if (yearMode === 'latest' && f.latest_year !== latest) return false;
      if (yearMode === '5y' && (f.latest_year ?? 0) < latest - 4) return false;
      return true;
    });
    const matches = searchFacilities(facilities, q, 100);
    renderResults(matches, q);
  }

  function renderResults(matches: Facility[], query: string) {
    clear(results);
    if (matches.length === 0) {
      results.appendChild(el('p', { class: 'text-tertiary mt-lg' }, query ? `No facilities matched "${query}".` : 'No facilities to show.'));
      return;
    }
    const wrap = el('div', { class: 'table-wrap' });
    const max = matches[0].total;
    const t = el('table', { class: 'data' }, [
      el('thead', null, [
        el('tr', null, [
          el('th', null, 'Facility'),
          el('th', null, 'Location'),
          el('th', null, 'Industry'),
          el('th', null, 'Latest year'),
          el('th', null, 'Lifetime release'),
          el('th', { style: 'width:140px' }, '')
        ])
      ]),
      el('tbody', null,
        matches.map(f => el('tr', {
          class: 'clickable',
          onclick: () => ctx.openFacility(f.id)
        }, [
          el('td', null, [
            el('div', null, f.name),
            el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs)' }, f.business_name)
          ]),
          el('td', null, [
            el('div', null, f.suburb || '—'),
            el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs)' }, [
              el('span', {
                class: 'pill pill-state',
                style: `background:${stateColor(f.state)}22;color:${stateColor(f.state)};border-color:${stateColor(f.state)}40`
              }, f.state || '—'),
              ' ',
              f.postcode || ''
            ])
          ]),
          el('td', { class: 'text-secondary', style: 'font-size: var(--font-size-xs)' }, f.anzsic_class_name),
          el('td', { class: 'num' }, formatYear(f.latest_year)),
          el('td', { class: 'num' }, formatMass(f.total)),
          el('td', null, [bar(f.total / max, divisionColor(f.division_code))])
        ]))
      )
    ]);
    wrap.appendChild(t);
    results.appendChild(wrap);
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(update, 200);
  });
  stateSelect.addEventListener('change', update);
  yearSelect.addEventListener('change', update);

  // Show top results initially
  update();
  setTimeout(() => input.focus(), 50);
}

function bar(frac: number, color: string): HTMLElement {
  const w = Math.max(2, Math.min(100, frac * 100));
  return el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 8px; overflow: hidden;' }, [
    el('div', { style: `background: ${color}; height: 100%; width: ${w}%;` })
  ]);
}
